import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Building2, Users, Stethoscope } from "lucide-react";

export default function Departments() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const fetchDepts = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchDepts(); }, [fetchDepts]);

  const openAdd = () => { setForm({ name: "", description: "" }); setEditDept(null); setShowModal(true); };
  const openEdit = (d) => { setForm({ name: d.name, description: d.description || "" }); setEditDept(d); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editDept) {
        await api.put(`/departments/${editDept.id}`, form);
        toast({ title: "Department updated" });
      } else {
        await api.post("/departments", form);
        toast({ title: "Department created" });
      }
      setShowModal(false); fetchDepts();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await api.delete(`/departments/${id}`);
      toast({ title: "Department deleted" });
      fetchDepts();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const deptColors = [
    "bg-emerald-50 border-emerald-200 text-emerald-800",
    "bg-sky-50 border-sky-200 text-sky-800",
    "bg-violet-50 border-violet-200 text-violet-800",
    "bg-amber-50 border-amber-200 text-amber-800",
    "bg-rose-50 border-rose-200 text-rose-800",
    "bg-teal-50 border-teal-200 text-teal-800",
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("departments")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage clinic departments</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-dept-btn">
          <Plus size={16} /> Add Department
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : departments.length === 0 ? (
        <div className="text-center py-20">
          <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No departments yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map((dept, idx) => (
            <div key={dept.id} className={`bg-white rounded-xl border ${deptColors[idx % deptColors.length].split(" ").slice(1).join(" ")} p-5 stat-card`} data-testid={`dept-card-${dept.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-11 h-11 ${deptColors[idx % deptColors.length].split(" ").slice(0,2).join(" ")} rounded-xl flex items-center justify-center border`}>
                  <Building2 size={20} className={deptColors[idx % deptColors.length].split(" ").slice(2).join(" ")} />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(dept)} data-testid={`edit-dept-${dept.id}`}>
                    <Edit size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(dept.id)} data-testid={`delete-dept-${dept.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
              <h3 className="text-base font-bold text-slate-900 font-sans">{dept.name}</h3>
              {dept.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{dept.description}</p>}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Stethoscope size={12} />
                  <span>{dept.doctors?.length || 0} doctors</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Users size={12} />
                  <span>{dept.staff?.length || 0} staff</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans">{editDept ? "Edit Department" : "Add Department"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Department Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Cardiology, ENT, Radiology" className="mt-1 border-slate-200" required data-testid="dept-name-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Description</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description" className="mt-1 border-slate-200" data-testid="dept-desc-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-dept-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
